import React, { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { supabase } from "../supabaseClient";
import {
  LogIn,
  LogOut,
  User,
  Menu,
  X,
  CheckCircle,
  ChevronDown,
  Briefcase,
  PlayCircle,
} from "lucide-react";

const Navbar = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsOpen, setIsToolsOpen] = useState(false);
  const prefersReducedMotion = useReducedMotion();

  const toolsRef = useRef(null);

  useEffect(() => {
    const getSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) fetchUserProfile(currentUser.email);
    };

    getSession();

    const { data: listener } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const currentUser = session?.user ?? null;
        setUser(currentUser);

        if (currentUser) fetchUserProfile(currentUser.email);
        else setIsPremium(false);
      }
    );

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target)) {
        setIsToolsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsToolsOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsMobileMenuOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [isMobileMenuOpen]);

  const fetchUserProfile = async (email) => {
    const { data, error } = await supabase
      .from("user_profiles")
      .select("is_unlimited")
      .eq("email", email)
      .single();

    setIsPremium(!error && !!data?.is_unlimited);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsMobileMenuOpen(false);
    setIsToolsOpen(false);
  };

  const baseLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/jobs", label: "Jobs" },
    { path: "/pricing", label: "Pricing" },
  ];

  const toolsLinks = [
    { path: "/upload", label: "Resume Optimizer" },
    { path: "/cover-letter", label: "Cover Letter" },
    { path: "/interview-prep", label: "Interview Prep" },
    { path: "/expert-review", label: "Expert CV Review" },
    { path: "/zam-agent", label: "Zam Agent" },
  ];

  const demoHref = "https://www.youtube.com/watch?v=1_w86olANTY";

  const isActive = (path) => {
    const current = location.pathname;

    if (path === "/") {
      return current === "/"
        ? "text-blue-600 font-semibold"
        : "text-gray-700 hover:text-blue-500";
    }

    return current === path || current.startsWith(`${path}/`)
      ? "text-blue-600 font-semibold"
      : "text-gray-700 hover:text-blue-500";
  };

  const isToolsActive = () => {
    const current = location.pathname;
    const active = toolsLinks.some(
      (l) => current === l.path || current.startsWith(`${l.path}/`)
    );

    return active
      ? "text-blue-600 font-semibold"
      : "text-gray-700 hover:text-blue-500";
  };

  const pillBase =
    "h-9 px-4 rounded-full text-sm font-semibold shadow-md flex items-center gap-2 whitespace-nowrap shrink-0";

  const premiumButton = isPremium ? (
    <span className={`${pillBase} bg-green-500 text-white`}>
      <CheckCircle className="w-4 h-4" />
      Premium Active
    </span>
  ) : (
    <Link
      to="/pricing"
      className={`${pillBase} bg-yellow-500 hover:bg-yellow-600 text-white transition`}
      onClick={() => setIsMobileMenuOpen(false)}
    >
      Go Premium
    </Link>
  );

  return (
    <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 py-3 flex justify-between items-center gap-4">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="shrink-0"
        >
          <Link
            to="/"
            className="flex items-center gap-2"
            onClick={() => {
              setIsMobileMenuOpen(false);
              setIsToolsOpen(false);
            }}
          >
            <img
              src="/logo-symbol.png"
              alt="JobZam Logo"
              width="40"
              height="40"
              className="w-8 h-8 md:w-10 md:h-10"
            />
            <span className="text-lg md:text-xl font-extrabold text-transparent bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text">
              JobZam
            </span>
          </Link>
        </motion.div>

        <div className="hidden lg:flex items-center justify-between flex-1">
          <ul className="flex space-x-6 text-sm font-medium items-center xl:space-x-7">
            {baseLinks.map(({ path, label }) => (
              <motion.li
                key={path}
                whileHover={{ scale: 1.05 }}
                className={`transition-all ${isActive(path)}`}
              >
                <Link to={path}>{label}</Link>
              </motion.li>
            ))}

            <li ref={toolsRef} className="relative">
              <div className="relative group">
                <button
                  type="button"
                  onClick={() => setIsToolsOpen((v) => !v)}
                  className={`flex items-center gap-1 transition-all ${isToolsActive()}`}
                  aria-haspopup="menu"
                  aria-expanded={isToolsOpen}
                >
                  <span className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    Career Tools
                  </span>
                  <ChevronDown
                    className={`w-4 h-4 transition-transform ${
                      isToolsOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`
                    absolute top-10 left-0 w-64 bg-white border border-gray-200 shadow-xl rounded-xl overflow-hidden
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible
                    transition-all duration-150
                    ${isToolsOpen ? "opacity-100 visible" : ""}
                  `}
                >
                  {toolsLinks.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsToolsOpen(false)}
                      className={`block px-4 py-3 text-sm hover:bg-gray-50 transition ${
                        location.pathname === item.path
                          ? "text-blue-600 font-semibold"
                          : "text-gray-700"
                      }`}
                    >
                      {item.label}
                    </Link>
                  ))}
                </motion.div>
              </div>
            </li>
          </ul>

          <div className="flex items-center gap-3 flex-nowrap">
            <a
              href={demoHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-9 shrink-0 items-center gap-2 rounded-full border border-blue-100 bg-white/80 px-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:border-blue-200 hover:text-blue-700 hover:shadow-md"
            >
              <PlayCircle className="h-4 w-4 text-blue-600" />
              Watch Demo
            </a>

            {user ? (
              <>
                {premiumButton}

                <div className="hidden xl:flex items-center gap-2 text-blue-600 font-semibold max-w-[220px]">
                  <User className="w-4 h-4 shrink-0" />
                  <span className="truncate">{user.email}</span>
                </div>

                <button
                  onClick={handleLogout}
                  className="h-9 px-4 rounded-md text-sm flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 transition shrink-0"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </>
            ) : (
              <Link
                to="/login"
                className="h-9 px-4 rounded-md text-sm flex items-center gap-2 bg-blue-500 text-white hover:bg-blue-600 transition shrink-0"
              >
                <LogIn className="w-4 h-4" />
                Login
              </Link>
            )}
          </div>
        </div>

        <div className="lg:hidden shrink-0">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl text-slate-700 transition hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100"
            aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation-menu"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>
      </div>

      <AnimatePresence initial={false}>
      {isMobileMenuOpen && (
        <motion.div
          id="mobile-navigation-menu"
          initial={prefersReducedMotion ? false : { opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -6 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.16, ease: "easeOut" }}
          className="max-h-[calc(100dvh-68px)] space-y-1.5 overflow-y-auto border-t border-slate-100 bg-white/95 px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 text-sm font-medium shadow-xl backdrop-blur-md lg:hidden"
        >
          {baseLinks.map(({ path, label }) => (
            <Link
              key={path}
              to={path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`flex min-h-11 items-center rounded-xl px-3 outline-none transition hover:bg-blue-50 focus-visible:ring-4 focus-visible:ring-blue-100 ${isActive(path)}`}
            >
              {label}
            </Link>
          ))}

          <div className="mt-1 border-t border-gray-200 pt-3">
            <div className="mb-1.5 flex items-center gap-2 px-3 text-xs font-semibold uppercase text-gray-500">
              <Briefcase className="w-4 h-4" />
              Career Tools
            </div>

            <div className="space-y-1">
              {toolsLinks.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex min-h-11 items-center rounded-xl px-3 outline-none transition hover:bg-blue-50 focus-visible:ring-4 focus-visible:ring-blue-100 ${isActive(item.path)}`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>

          <a
            href={demoHref}
            target="_blank"
            rel="noreferrer"
            onClick={() => setIsMobileMenuOpen(false)}
            className="mt-1 flex min-h-11 items-center gap-2 border-t border-gray-200 px-3 pt-2 text-slate-700 outline-none transition hover:text-blue-600 focus-visible:ring-4 focus-visible:ring-blue-100"
          >
            <PlayCircle className="w-4 h-4 text-blue-600" />
            Watch Demo
          </a>

          {user ? (
            <>
              <div className="pt-3 border-t border-gray-200">{premiumButton}</div>

              <div className="text-blue-600 font-semibold flex items-center gap-2">
                <User className="w-4 h-4" />
                <span className="truncate">{user.email}</span>
              </div>

              <button
                onClick={handleLogout}
                className="h-9 px-4 rounded-md text-sm flex items-center gap-2 bg-red-500 text-white hover:bg-red-600 transition"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </>
          ) : (
            <Link
              to="/login"
              onClick={() => setIsMobileMenuOpen(false)}
              className="mt-2 flex min-h-11 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-violet-600 px-4 text-sm font-bold text-white shadow-lg shadow-blue-100 transition hover:from-blue-700 hover:to-violet-700 focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-200"
            >
              <LogIn className="w-4 h-4" />
              Login
            </Link>
          )}
        </motion.div>
      )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
